import React, { useEffect, useState } from "react";
import _ from "lodash";
import Moment from "react-moment";
import "jspdf-autotable";
import { useLocation, useNavigate } from "react-router-dom";
import countryISDCodes from "../Services/countryCodes.json";
import { useDispatch, useSelector } from "react-redux";
import { getAppSummary, getAppUpdates, loadAppSummary, loadAppUpdates } from "../app/store/application";
import WorkflowElement from "../app/components/WorkflowElement";
import { setCurrentPageTitle, setCurrentRoute } from "../app/store/layoutUI";

const SUMMARY_STYLES = {
  pageWrap: {
    padding: "12px 0 4px 0"
  },
  shell: {
    borderRadius: "24px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    boxShadow: "0 18px 38px rgba(15, 23, 42, 0.06)",
    overflow: "hidden"
  },
  hero: {
    padding: "24px 24px 18px 24px",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    background: "linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(239, 246, 255, 0.95) 100%)"
  },
  heroRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    flexWrap: "wrap"
  },
  titleWrap: {
    minWidth: 0,
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    flex: "1"
  },
  titleIcon: {
    width: "52px",
    height: "52px",
    borderRadius: "16px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, rgba(37, 99, 235, 0.12) 0%, rgba(79, 70, 229, 0.16) 100%)",
    color: "#4338ca",
    fontSize: "24px",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)"
  },
  title: {
    fontSize: "24px",
    fontWeight: 800,
    lineHeight: 1.15,
    color: "#0f172a",
    margin: 0
  },
  subtitle: {
    fontSize: "14px",
    lineHeight: 1.6,
    color: "#475569",
    margin: "8px 0 0 0",
  },
  closeButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "999px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    color: "#334155",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  body: {
    padding: "20px 24px 24px 24px"
  }
};

export default function ApplicationSummary() {

  const [appSummary, setAppSummary] = useState("");
  const [wfTask, setWfTask] = useState(null);
  const [updatedStateData, setUpdatedStateData] = useState("");
  const [currentSequence, setCurrentSequence] = useState(1);
  const [codeIsVerified, setCodeIsVerified] = useState(true);
  const [viewType, setViewType] = useState("View");
  const [appUpdates, setAppUpdates] = useState([]);

  const appSummarySelector = useSelector(getAppSummary);
  const appUpdateSelector = useSelector(getAppUpdates);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const usrType = localStorage.getItem("userType");
  const pgTitle = usrType === "candidate" ? "Onboarding Summary" : "Enrollment Summary";

  const route = [
    { name: "Task Management", url: "" },
    { name: pgTitle, url: "" }
  ];

  useEffect(() => {
    dispatch(setCurrentPageTitle(pgTitle));
    dispatch(setCurrentRoute(route));

    const state = location.state;
    if (state?.task) {
      dispatch(loadAppSummary(state.task.ciid));
      dispatch(loadAppUpdates(state.task.ciid));
    }

    if (state?.viewType === "edit") {
      setViewType("Edit");
    } else if (state?.viewType === "editnId") {
      setViewType("Edited");
    } else {
      setViewType("View");
    }
  }, []);

  useEffect(() => {
    if (appSummarySelector !== "") {
      setAppSummary(appSummarySelector);
      const state = location.state;
      let task = { formId: 0, ciid: state.task.ciid };
      const frm = appSummarySelector.applicationForms.filter(x => x.formSequence === currentSequence);
      if (frm.length > 0) {
        task.formId = frm[0].formId;
        setWfTask(task);
      }
      if (appSummarySelector?.applicationData)
        setUpdatedStateData(appSummarySelector.applicationData);
    }

    if (appUpdateSelector.length > 0) {
      setAppUpdates(appUpdateSelector);
      const lastUpdated = appUpdateSelector[appUpdateSelector.length - 1];
      setUpdatedStateData(lastUpdated.stateData);
    }
  }, [appSummarySelector, appUpdateSelector]);

  const handleNextTask = () => {
    dispatch(loadAppUpdates(wfTask.ciid))
  };

  return (
    <React.Fragment>
      <div className="col-md-12" style={SUMMARY_STYLES.pageWrap}>
        <style>{`
          .close-summary-btn:hover {
            background-color: #f8fafc !important;
            border-color: #cbd5e1 !important;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          }
          .close-summary-btn:active {
            transform: translateY(0);
          }
        `}</style>
        <div style={SUMMARY_STYLES.shell}>
          <div style={SUMMARY_STYLES.hero}>
            <div style={SUMMARY_STYLES.heroRow}>
              <div style={SUMMARY_STYLES.titleWrap}>
                <div style={SUMMARY_STYLES.titleIcon}>
                  <i className="fa fa-file-text-o" aria-hidden="true"></i>
                </div>
                <div className="min-width-0">
                  <h1 style={SUMMARY_STYLES.title}>{pgTitle}</h1>
                  <p style={SUMMARY_STYLES.subtitle}>
                    Review the complete history, submitted data, and current status of the {usrType === "candidate" ? "onboarding" : "enrollment"} process.
                  </p>
                </div>
              </div>
              <button
                className="close-summary-btn"
                style={SUMMARY_STYLES.closeButton}
                onClick={() => navigate(-1)}
              >
                <i className="fa fa-times" aria-hidden="true"></i> Close
              </button>
            </div>
          </div>
          <div style={SUMMARY_STYLES.body}>
            {wfTask?.formId > 0 && (
              <WorkflowElement
                wfTask={wfTask}
                handleNextTask={handleNextTask}
                viewType={viewType === "View" ? true : false}
                viewTypeType={viewType}
                appSummary={true}
                codeVerified={codeIsVerified}
                updatedStateData={updatedStateData}
                actionType="upd"
              />
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

